import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getCampaigns, deleteCampaign } from '../api/whatsapp';

const statusConfig = {
  draft: { label: 'Draft', color: '#64748b', icon: <PendingIcon sx={{ fontSize: 16 }} /> },
  scheduled: { label: 'Scheduled', color: '#3b82f6', icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
  sent: { label: 'Sent', color: '#22c55e', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
  failed: { label: 'Failed', color: '#ef4444', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
  cancelled: { label: 'Cancelled', color: '#f59e0b', icon: <CancelIcon sx={{ fontSize: 16 }} /> },
};

const Campaigns = ({ onNavigateCreate }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getCampaigns().then(d => setCampaigns(d.campaigns || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteCampaign(deleteId);
      setCampaigns(prev => prev.filter(c => c._id !== deleteId));
    } catch {}
    setDeleting(false);
    setDeleteId(null);
  };

  return (
    <Box sx={{ p: 4, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Campaigns</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNavigateCreate}
            sx={{ borderRadius: 2 }}>
            Create Campaign
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
        ) : campaigns.length === 0 ? (
          <Paper className="glass-card" sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
            <CampaignIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h6" color="#1e293b" fontWeight={700} gutterBottom>No campaigns yet</Typography>
            <Typography variant="body2" color="#64748b" mb={3}>Create your first WhatsApp campaign to start reaching your customers with template messages.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onNavigateCreate}
              sx={{ borderRadius: 2, px: 4 }}>
              Create Campaign
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper} className="glass-card" sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Template</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Recipients</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Error</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map(c => {
                  const sc = statusConfig[c.status] || statusConfig.draft;
                  return (
                    <TableRow key={c._id} hover
                      sx={{ '&:hover': { bgcolor: 'rgba(7,94,84,0.04)' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                      <TableCell>{c.templateName || '—'}</TableCell>
                      <TableCell>{c.recipientType === 'all' ? `All Contacts (${c.recipientCount})` : 'Group'}</TableCell>
                      <TableCell>
                        <Chip icon={sc.icon} label={sc.label} size="small"
                          sx={{ color: '#fff', bgcolor: sc.color, fontWeight: 600, fontSize: 11, borderRadius: '6px' }} />
                      </TableCell>
                      <TableCell>{c.status === 'sent' || c.status === 'failed' ? `${c.successCount}/${c.recipientCount}` : '—'}</TableCell>
                      <TableCell>
                        {c.lastError ? (
                          <Tooltip title={c.lastError} arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#ef4444', cursor: 'help' }} />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#64748b">
                          {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete campaign" arrow>
                          <IconButton size="small" onClick={() => setDeleteId(c._id)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)}
          PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>Delete Campaign</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="#64748b">Are you sure you want to delete this campaign? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setDeleteId(null)} disabled={deleting} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} variant="contained" color="error" sx={{ borderRadius: 2 }}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Campaigns;
